import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Col, Row, Label } from "reactstrap";
import APLStudentEventCard from "./APLStudentEventCard";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage, useIntl } from "react-intl";

// Moved outside component to avoid recreation on every render
const ITEMS_PER_BATCH = 50;

const APLMoments = ({ studentEvents, level, periodId, objectId }) => {
  const intl = useIntl();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [eventStatuses, setEventStatuses] = useState([]);
  const [workSteps, setWorkSteps] = useState([]);
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_BATCH);
  const sentinelRef = useRef(null);
  const events = useMemo(
    () => studentEvents?.data || [],
    [studentEvents?.data],
  );

  const statusOptions = [
    {
      value: "SignedBySupervisor",
      label: intl.formatMessage({ id: "SignedBySupervisor" }),
    },
    {
      value: "UnsignedBySupervisor",
      label: intl.formatMessage({ id: "UnsignedBySupervisor" }),
    },
    {
      value: "SignedByTeacher",
      label: intl.formatMessage({ id: "SignedByTeacher" }),
    },
    {
      value: "UnsignedByTeacher",
      label: intl.formatMessage({ id: "UnsignedByTeacher" }),
    },
    {
      value: "Commented",
      label: intl.formatMessage({ id: "Commented" }),
    },
  ];

  const filterWorkSteps = useMemo(
    () => [
      ...new Map(
        events
          .map((event) => event.workStepComponent?.workStepName)
          .filter(Boolean)
          .map((workStepName) => ({
            label: workStepName,
            value: workStepName,
          }))
          .map((item) => [item.value, item]),
      ).values(),
    ],
    [events],
  );

  const handleResetAll = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setEventStatuses([]);
    setWorkSteps([]);
  }, []);

  const handleClearDates = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
  }, []);

  // Replaced useEffect + setState with useMemo for better performance
  const hasActiveFilters =
    eventStatuses.length > 0 ||
    workSteps.length > 0 ||
    Boolean(startDate) ||
    Boolean(endDate);

  const sortedEvents = useMemo(() => {
    let filteredEvents = [...events];

    if (startDate && endDate) {
      const toDateOnly = (d) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const start = toDateOnly(startDate);
      const end = toDateOnly(endDate);

      filteredEvents = filteredEvents.filter((item) => {
        const createdDate = toDateOnly(new Date(item.created));
        return createdDate >= start && createdDate <= end;
      });
    }

    if (eventStatuses && eventStatuses.length > 0) {
      filteredEvents = filteredEvents.filter((item) => {
        const statusFilters = {
          SignedBySupervisor: item.signedBySupervisor,
          UnsignedBySupervisor: !item.signedBySupervisor,
          SignedByTeacher: item.signedByTeacher,
          UnsignedByTeacher: !item.signedByTeacher,
          Commented: item.comments.length > 0,
        };
        return eventStatuses.some((status) => statusFilters[status.value]);
      });
    }

    if (workSteps && workSteps.length > 0) {
      filteredEvents = filteredEvents.filter((item) =>
        workSteps.some(
          (step) => step.value === item.workStepComponent.workStepName
        )
      );
    }

    filteredEvents.sort((a, b) => {
      const c = new Date(a.created);
      const d = new Date(b.created);
      return d - c;
    });

    return filteredEvents;
  }, [eventStatuses, workSteps, startDate, endDate, events]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(ITEMS_PER_BATCH);
  }, [eventStatuses, workSteps, startDate, endDate, events.length]);

  // IntersectionObserver to load more items on scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + ITEMS_PER_BATCH);
        }
      },
      { rootMargin: "400px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sortedEvents]);

  const visibleEvents = sortedEvents.slice(0, visibleCount);

  return (
    <div className="mx-auto w-100 apl-moments--container">
      <div className="apl-moments--filter-panel">
        <div className="d-flex justify-content-between align-items-center mb-2 apl-moments--filter-header">
          <span
            style={{ fontSize: "0.9rem", fontWeight: 500, color: "#374151" }}
          >
            <FormattedMessage id="FilterEventsBy" />
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              className="apl-moments--reset-btn"
              onClick={handleResetAll}
            >
              <FontAwesomeIcon icon={faXmark} className="me-1" />
              <FormattedMessage id="ResetAll" />
            </button>
          )}
        </div>

        <Row className="g-2">
          <Col md={4}>
            <Label className="form-label mb-1">
              <FormattedMessage id="Date" />
            </Label>
            <DatePicker
              wrapperClassName="d-block"
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={([start, end]) => {
                setStartDate(start);
                setEndDate(end);
              }}
              dateFormat="yyyy-MM-dd"
              locale="sv"
              className="form-control apl-moments--date-input"
              placeholderText={intl.formatMessage({
                id: "SelectDateRange",
              })}
              isClearable
            />
          </Col>
          <Col md={4}>
            <Label className="form-label mb-1">
              <FormattedMessage id="Status" />
            </Label>
            <Select
              options={statusOptions}
              isClearable
              isMulti
              placeholder={intl.formatMessage({ id: "SelectStatus" })}
              value={eventStatuses}
              onChange={(selected) => setEventStatuses(selected || [])}

            />
          </Col>
          <Col md={4}>
            <Label className="form-label mb-1">
              <FormattedMessage id="Workstep" />
            </Label>
            <Select
              options={filterWorkSteps}
              isClearable
              isMulti
              placeholder={intl.formatMessage({ id: "SelectWorkstep" })}
              value={workSteps}
              onChange={(selected) => setWorkSteps(selected || [])}

            />
          </Col>
        </Row>

        {hasActiveFilters && (
          <div className="pt-2">
            <small className="text-muted me-2">
              <FormattedMessage id="ActiveFilters" />
            </small>
            {startDate && endDate && (
              <span className="apl-moments--filter-chip">
                {`${startDate.toLocaleDateString("sv-SE")} - ${endDate.toLocaleDateString("sv-SE")}`}
                <button type="button" onClick={handleClearDates}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            )}
            {eventStatuses.map((status) => (
              <span key={status.value} className="apl-moments--filter-chip">
                {status.label}
                <button
                  type="button"
                  onClick={() =>
                    setEventStatuses(
                      eventStatuses.filter((s) => s.value !== status.value),
                    )
                  }
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
            {workSteps.map((step) => (
              <span key={step.value} className="apl-moments--filter-chip">
                {step.label}
                <button
                  type="button"
                  onClick={() =>
                    setWorkSteps(
                      workSteps.filter((s) => s.value !== step.value),
                    )
                  }
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="apl-moments--count mt-2">
          {sortedEvents.length} <FormattedMessage id="Of" /> {events.length}{" "}
          <FormattedMessage id="WorkstepsFound" />
        </div>
      </div>

      <div className="pb-5 apl-moments--feed">
        {visibleEvents.map((item) => {
          const date = new Date(item.created);
          return (
            <APLStudentEventCard
              key={item.id}
              item={item}
              date={date}
              level={level}
              periodId={periodId}
              objectId={objectId}
            />
          );
        })}
        {visibleCount < sortedEvents.length && (
          <div className="apl-moments--feed-footer">
            <div ref={sentinelRef} style={{ height: "1px" }} />
            <button
              type="button"
              className="apl-moments--show-all-btn"
              onClick={() => setVisibleCount(sortedEvents.length)}
            >
              <FormattedMessage id="ShowAll" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default APLMoments;
